/**
 * VaultDocumentCard.jsx — Individual vault document card.
 *
 * Shows: category badge, title, date, file size.
 * Actions: Copy Link, Toggle Public/Private, Download, Delete.
 */
import React, { useState } from "react";
import { Download, Trash2, Link2, Lock, Globe, FileText, Award, Briefcase, GraduationCap, FolderOpen } from "lucide-react";

const CATEGORY_META = {
  certificate:       { label: "Certificate",        icon: Award,         color: "#8B5CF6" },
  experience_letter: { label: "Experience Letter",  icon: Briefcase,     color: "#0EA5E9" },
  offer_letter:      { label: "Offer Letter",       icon: Briefcase,     color: "#10B981" },
  education:         { label: "Education",          icon: GraduationCap, color: "#F59E0B" },
  internship:        { label: "Internship",         icon: Briefcase,     color: "#06B6D4" },
  award:             { label: "Award",              icon: Award,         color: "#EF4444" },
  project:           { label: "Project",            icon: FolderOpen,    color: "#8B5CF6" },
  patent:            { label: "Patent",             icon: FileText,      color: "#6366F1" },
  publication:       { label: "Publication",        icon: FileText,      color: "#EC4899" },
  license:           { label: "License",            icon: Award,         color: "#14B8A6" },
  identity:          { label: "Identity Document",  icon: FileText,      color: "#F97316" },
  other:             { label: "Other",              icon: FileText,      color: "#6B7280" },
};

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function VaultDocumentCard({ doc, theme, onToggleVisibility, onDownload, onDelete, onCopyLink }) {
  const isDark = theme === "dark";
  const [copied, setCopied] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const meta = CATEGORY_META[doc.category] || CATEGORY_META.other;
  const CategoryIcon = meta.icon;

  const handleCopy = () => {
    onCopyLink(doc);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleToggle = async () => {
    setToggling(true);
    await onToggleVisibility(doc);
    setToggling(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    await onDelete(doc.id);
  };

  const cardBg     = isDark ? "#111113" : "#FFFFFF";
  const borderColor= isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const textPrimary= isDark ? "#F4F4F5" : "#111827";
  const textMuted  = isDark ? "#71717A" : "#6B7280";
  const btnHover   = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${borderColor}`,
        borderRadius: 14,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        transition: "box-shadow 0.2s ease, transform 0.15s ease",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = isDark
          ? "0 4px 24px rgba(0,0,0,0.45)"
          : "0 4px 20px rgba(0,0,0,0.10)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Accent line */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: meta.color, borderRadius: "14px 14px 0 0" }} />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, paddingTop: 4 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8, flexShrink: 0,
          background: `${meta.color}18`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <CategoryIcon size={16} color={meta.color} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: textPrimary, margin: 0, lineHeight: 1.3, wordBreak: "break-word" }}>
            {doc.title}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20,
              background: `${meta.color}18`, color: meta.color, letterSpacing: "0.02em",
            }}>
              {meta.label}
            </span>
            {doc.is_missing && (
              <span style={{
                fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20,
                background: "#EF444418", color: "#EF4444", letterSpacing: "0.02em",
              }}>
                File Missing
              </span>
            )}
            {doc.file_size && (
              <span style={{ fontSize: 10, color: textMuted }}>{formatSize(doc.file_size)}</span>
            )}
            <span style={{ fontSize: 10, color: textMuted }}>{formatDate(doc.upload_date)}</span>
          </div>
        </div>

        {/* Visibility badge */}
        <div style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 10, fontWeight: 600,
          color: doc.is_public ? "#10B981" : textMuted,
          padding: "3px 8px", borderRadius: 20,
          background: doc.is_public ? "#10B98118" : (isDark ? "#27272A" : "#F4F4F5"),
          flexShrink: 0,
        }}>
          {doc.is_public ? <Globe size={9} /> : <Lock size={9} />}
          {doc.is_public ? "Public" : "Private"}
        </div>
      </div>

      {/* Notes */}
      {doc.notes && (
        <p style={{ fontSize: 11, color: textMuted, margin: 0, paddingLeft: 44, lineHeight: 1.5 }}>
          {doc.notes}
        </p>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 6, paddingLeft: 44 }}>
        {/* Copy link */}
        <button
          onClick={handleCopy}
          disabled={copied || doc.is_missing}
          title={doc.is_missing ? "File missing from server" : (doc.is_public ? "Copy share link" : "Make public to share")}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            fontSize: 11, fontWeight: 500,
            padding: "5px 10px", borderRadius: 8, border: "none",
            cursor: doc.is_missing ? "not-allowed" : "pointer",
            background: copied ? "#10B98118" : (isDark ? "#27272A" : "#F4F4F5"),
            color: copied ? "#10B981" : textMuted,
            transition: "all 0.15s ease",
            opacity: doc.is_missing ? 0.4 : 1,
          }}
        >
          <Link2 size={11} />
          {copied ? "Copied!" : "Copy Link"}
        </button>

        {/* Toggle public/private */}
        <button
          onClick={handleToggle}
          disabled={toggling || doc.is_missing}
          title={doc.is_missing ? "File missing from server" : (doc.is_public ? "Make private" : "Make public")}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            fontSize: 11, fontWeight: 500,
            padding: "5px 10px", borderRadius: 8, border: "none",
            cursor: doc.is_missing ? "not-allowed" : "pointer",
            background: isDark ? "#27272A" : "#F4F4F5",
            color: textMuted,
            transition: "all 0.15s ease",
            opacity: doc.is_missing ? 0.4 : (toggling ? 0.5 : 1),
          }}
        >
          {doc.is_public ? <Lock size={11} /> : <Globe size={11} />}
          {toggling ? "…" : (doc.is_public ? "Make Private" : "Make Public")}
        </button>

        {/* Download */}
        <button
          onClick={() => onDownload(doc)}
          disabled={doc.is_missing}
          title={doc.is_missing ? "File missing from server" : "Download"}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, borderRadius: 8, border: "none",
            cursor: doc.is_missing ? "not-allowed" : "pointer",
            background: isDark ? "#27272A" : "#F4F4F5",
            color: textMuted,
            transition: "all 0.15s ease",
            opacity: doc.is_missing ? 0.4 : 1,
          }}
        >
          <Download size={12} />
        </button>

        {/* Delete */}
        <button
          onClick={handleDelete}
          disabled={deleting}
          title="Delete"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, borderRadius: 8, border: "none", cursor: "pointer",
            background: isDark ? "#27272A" : "#F4F4F5",
            color: deleting ? textMuted : "#EF4444",
            transition: "all 0.15s ease",
            opacity: deleting ? 0.5 : 1,
          }}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}
